/**
 * Central query-key factory.
 *
 * Ad-hoc `['pond', pondId, farmId]` vs `['pond', 'detail', pondId, farmId]` shapes
 * were causing two problems (issue #89):
 *   1. Duplicate cache entries for the same endpoint (wasted refetches after
 *      navigating entry → detail).
 *   2. `invalidateQueries` calls missed one shape or the other, so mutations
 *      only evicted some callers.
 *
 * Every query in the app should source its key from here. To extend, add the
 * new endpoint family alongside the existing ones — keep the shape stable so
 * `invalidateQueries` at a higher scope (e.g. `qk.pond.all()`) still sweeps it.
 */

export const qk = {
  pond: {
    /** Sweeps every pond-scoped query for a given farm. */
    all: (farmId: string | null | undefined) => ['pond', farmId] as const,
    detail: (farmId: string | null | undefined, pondId: string) =>
      ['pond', farmId, pondId, 'detail'] as const,
    siblings: (farmId: string | null | undefined, pondId: string) =>
      ['pond', farmId, pondId, 'siblings'] as const,
    entryStatus: (pondId: string, dateStr: string) =>
      ['pond', 'entry-status', pondId, dateStr] as const,
  },

  readings: {
    day: (farmId: string | undefined, cycleId: string, dateStr: string) =>
      ['readings', farmId, cycleId, 'day', dateStr] as const,
    list: (farmId: string | undefined, cycleId: string) =>
      ['readings', farmId, cycleId] as const,
  },

  feedings: {
    day: (farmId: string | undefined, cycleId: string, dateStr: string) =>
      ['feedings', farmId, cycleId, 'day', dateStr] as const,
  },

  samplings: {
    list: (farmId: string | undefined, cycleId: string) =>
      ['samplings', farmId, cycleId] as const,
    latestBefore: (farmId: string | undefined, cycleId: string, dateStr: string) =>
      ['samplings', farmId, cycleId, 'latest-before', dateStr] as const,
  },

  harvests: {
    list: (farmId: string | undefined, cycleId: string) =>
      ['harvests', farmId, cycleId] as const,
  },

  thresholds: {
    farm: (farmId: string | null | undefined) => ['thresholds', farmId] as const,
  },

  feedingSchedule: {
    farm: (farmId: string | null | undefined) => ['feeding-schedule', farmId] as const,
  },
  farms: {
    list: (userId: string | undefined) => ['farms', userId] as const,
  },

  applications: {
    crop: (farmId: string | undefined, cropId: string) => ['applications', farmId, cropId] as const,
  },

  milestones: {
    crop: (farmId: string | undefined, cropId: string) => ['milestones', farmId, cropId] as const,
  },

  waterChecks: {
    crop: (farmId: string | undefined, cropId: string) => ['water-checks', farmId, cropId] as const,
  },

  editRequests: {
    /** `{ pending, approved, rejected }` for a farm. */
    counts: (farmId: string | null | undefined) => ['edit-requests-counts', farmId] as const,
  },

  pondNames: {
    /** `{ [cycleId]: pondName }` for a farm; a map, not the ponds array, so it keeps its own key. */
    byCycle: (farmId: string | undefined) => ['pond-names', farmId] as const,
  },

  ponds: {
    /** Array of every pond in the farm; the only shape any consumer may store under this key. */
    list: (farmId: string | null | undefined) => ['ponds', farmId] as const,
  },

  products: {
    list: (farmId: string | undefined) => ['products', farmId] as const,
  },

  farmUsers: {
    list: (farmId: string | undefined) => ['farm-users', farmId] as const,
  },

  membership: {
    current: (userId: string | undefined, farmId: string | undefined) =>
      ['membership', userId, farmId] as const,
  },
};
